"use client";

import { Sizes } from "@JohnLewisPartnership/jl-design-system/dist/components/size/SizeList";
import { useEffect, useMemo, useRef, useState } from "react";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/v2/Message";
import smoothscroll from "smoothscroll-polyfill";
import {
  NO_VARIANT_CODE,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
  WARNING_OR_ERROR_EVENT,
} from "../../constants/constants";
import { ButtonSizeThreshold, Size as SizeType } from "./types";
import styles from "./Sizes.module.scss";
import { SizeItems } from "./SizeItems";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const getKeyByThreshold = (
  number: number,
  buttonSizeStyleThreshold: ButtonSizeThreshold,
) => {
  for (const [key, threshold] of Object.entries(buttonSizeStyleThreshold)) {
    if (number <= threshold) {
      return key;
    }
  }

  return "large";
};

type Props = {
  /**
   * An array of unique sizes, each having a name, sublabel, sizeReductionLabel, showReduced, unavailable, and isSelected properties.
   */
  sizes: SizeType[];
  /**
   * A boolean to toggle ability to group sizes based on sizeHeadline.
   */
  isGroupingAllowed?: boolean;
  /**
   * A number which tells the component how many sizes to display.
   */
  showMore?: number;
  /**
   * An object containing the configuration for the button size rendered.
   */
  buttonSizeStyleThreshold?: ButtonSizeThreshold;
};
export function SizesUI({
  sizes,
  isGroupingAllowed = false,
  showMore,
  buttonSizeStyleThreshold = {
    small: 5,
    medium: 12,
    large: 99,
  },
}: Props) {
  const [hasSizeError, setHasSizeError] = useState(false);
  const errorMessageRef = useRef<HTMLDivElement | null>(null);
  const [buttonSizeStyle, groupedSizes] = useMemo(() => {
    let longestLabel = 0;
    const groupedSizes: Record<string, SizeType[]> = {};

    sizes.forEach((size) => {
      longestLabel = Math.max(size.name?.length || 0, longestLabel);
      groupedSizes[size.sizeHeadline] = [
        ...(groupedSizes[size.sizeHeadline] || []),
        size,
      ];
    });

    return [
      getKeyByThreshold(longestLabel, buttonSizeStyleThreshold),
      groupedSizes,
    ];
  }, [sizes]);

  useEffect(() => setHasSizeError(false), [sizes]);

  useEffect(() => {
    const handleNoSizeSelectedError = () => {
      setHasSizeError(true);
    };

    const errorOrWarningHandler = (
      event: CustomEvent<{ errors: { code: string }[] }>,
    ) => {
      const data = event.detail;
      const { errors = [] } = data;
      const hasVariantError = errors.some(
        ({ code }) => code === NO_VARIANT_CODE,
      );

      if (hasVariantError) {
        setHasSizeError(true);
      }
    };

    window.addEventListener(
      SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
      handleNoSizeSelectedError,
    );

    window.addEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);

    return () => {
      window.removeEventListener(
        SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
        handleNoSizeSelectedError,
      );
      window.removeEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);
    };
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (hasSizeError) {
      smoothscroll.polyfill();
      const sizeErrorElement = errorMessageRef.current;
      if (sizeErrorElement) {
        const headerOffset = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--jl-header-height",
          ),
        );
        const elementPosition = sizeErrorElement.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  }, [hasSizeError]);

  if (!sizes.length) {
    return null;
  }

  const isGrouped = isGroupingAllowed && Object.keys(groupedSizes).length > 1;

  return (
    <>
      {hasSizeError && (
        <div id="size:error" data-testid="size:error" ref={errorMessageRef}>
          <Message
            type="error"
            title="Select a size"
            className={styles.message}
          />
        </div>
      )}
      <Sizes
        selectedLabel={sizes.filter((size) => size.isSelected)[0]?.name}
        sublabel={sizes.filter((size) => size.isSelected)[0]?.sublabel}
        onClick={() => {
          setHasSizeError(false);
        }}
        longestLabel={buttonSizeStyle}
        className={cx("groupList")}
      >
        {isGrouped ? (
          Object.entries(groupedSizes).map(([groupName, sizes]) => (
            <SizeItems
              key={groupName}
              sizes={sizes}
              showMore={showMore}
              groupName={groupName}
            />
          ))
        ) : (
          <SizeItems sizes={sizes} showMore={showMore} />
        )}
      </Sizes>
    </>
  );
}
